import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "xv_cockpit_token";

export const cockpit = axios.create({
    baseURL: `${BACKEND}/api/cockpit`,
});

cockpit.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (token) {
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
});

const CockpitAuthCtx = createContext({
    user: null,
    ready: false,
    login: () => {},
    logout: () => {},
    refresh: () => {},
});

export function CockpitAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem(STORAGE_KEY);
        if (!token) {
            setUser(null);
            setReady(true);
            return null;
        }
        try {
            const { data } = await cockpit.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            localStorage.removeItem(STORAGE_KEY);
            setUser(null);
            return null;
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password, totp) => {
        const { data } = await cockpit.post("/auth/login", {
            email,
            password,
            totp: totp || null,
        });
        if (data.requires_totp) return { requiresTotp: true };
        localStorage.setItem(STORAGE_KEY, data.access_token);
        await refresh();
        return { ok: true };
    };

    const logout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setUser(null);
    };

    return (
        <CockpitAuthCtx.Provider value={{ user, ready, login, logout, refresh }}>
            {children}
        </CockpitAuthCtx.Provider>
    );
}

export const useCockpitAuth = () => useContext(CockpitAuthCtx);
